/**
 * Users Database References
 *
 * Full user profiles are stored under clients
 * Path: /clients/{clientKey}/users/{userId}
 *
 * Includes subcollections:
 * - sessions: User's video sessions
 * - notifications: User's notifications
 */

import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  DocumentReference,
  CollectionReference,
  Query,
  DocumentData
} from 'firebase/firestore';
import { db } from '../../firebase';

/* ═══════════════════════════════════════════════════════════
   USERS COLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to the users collection for a client
 * Path: /clients/{clientKey}/directory/users/main
 */
export const UsersRef_Collection = (clientKey: string = 'athleto'): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'directory', 'users', 'main');
};

/**
 * Reference to a specific user document
 * Path: /clients/{clientKey}/directory/users/main/{userId}
 */
export const UserRef_Document = (
  userId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'directory', 'users', 'main', userId);
};

/* ═══════════════════════════════════════════════════════════
   USER SESSIONS SUBCOLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to a user's sessions collection
 * Path: /clients/{clientKey}/directory/users/main/{userId}/sessions
 */
export const UserSessionsRef_Collection = (
  userId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'directory', 'users', 'main', userId, 'sessions');
};

/**
 * Reference to a specific user session
 * Path: /clients/{clientKey}/directory/users/main/{userId}/sessions/{sessionId}
 */
export const UserSessionRef_Document = (
  userId: string,
  sessionId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'directory', 'users', 'main', userId, 'sessions', sessionId);
};

/**
 * Query for user's sessions ordered by date
 */
export const UserSessionsOrderedRef_Query = (
  userId: string,
  orderByField: string = 'created_at',
  orderDesc: boolean = true,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UserSessionsRef_Collection(userId, clientKey),
    orderBy(orderByField, orderDesc ? 'desc' : 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for user's public sessions
 */
export const UserPublicSessionsRef_Query = (
  userId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UserSessionsRef_Collection(userId, clientKey),
    where('is_public', '==', true),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/* ═══════════════════════════════════════════════════════════
   USER NOTIFICATIONS SUBCOLLECTION
   ═══════════════════════════════════════════════════════════ */

/**
 * Reference to a user's notifications collection
 * Path: /clients/{clientKey}/directory/users/main/{userId}/notifications
 */
export const UserNotificationsRef_Collection = (
  userId: string,
  clientKey: string = 'athleto'
): CollectionReference<DocumentData> => {
  return collection(db, 'clients', clientKey, 'directory', 'users', 'main', userId, 'notifications');
};

/**
 * Reference to a specific notification
 * Path: /clients/{clientKey}/directory/users/main/{userId}/notifications/{notificationId}
 */
export const UserNotificationRef_Document = (
  userId: string,
  notificationId: string,
  clientKey: string = 'athleto'
): DocumentReference<DocumentData> => {
  return doc(db, 'clients', clientKey, 'directory', 'users', 'main', userId, 'notifications', notificationId);
};

/**
 * Query for user's notifications ordered by date
 */
export const UserNotificationsOrderedRef_Query = (
  userId: string,
  limitCount: number = 20,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    UserNotificationsRef_Collection(userId, clientKey),
    orderBy('created_at', 'desc'),
    limit(limitCount)
  );
};

/**
 * Query for user's unread notifications
 */
export const UserUnreadNotificationsRef_Query = (
  userId: string,
  limitCount: number = 20,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  return query(
    UserNotificationsRef_Collection(userId, clientKey),
    where('read', '==', false),
    orderBy('created_at', 'desc'),
    limit(limitCount)
  );
};

/* ═══════════════════════════════════════════════════════════
   USERS QUERIES
   ═══════════════════════════════════════════════════════════ */

/**
 * Query for users ordered by a field
 */
export const UsersOrderedRef_Query = (
  orderByField: string = 'created_at',
  orderDesc: boolean = true,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UsersRef_Collection(clientKey),
    orderBy(orderByField, orderDesc ? 'desc' : 'asc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for users by role
 */
export const UsersByRoleRef_Query = (
  role: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UsersRef_Collection(clientKey),
    where('role', '==', role),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};

/**
 * Query for users by organization
 */
export const UsersByOrganizationRef_Query = (
  organizationId: string,
  limitCount?: number,
  clientKey: string = 'athleto'
): Query<DocumentData> => {
  const baseQuery = query(
    UsersRef_Collection(clientKey),
    where('organization_id', '==', organizationId),
    orderBy('created_at', 'desc')
  );

  if (limitCount) {
    return query(baseQuery, limit(limitCount));
  }

  return baseQuery;
};
